import React, { useCallback, useEffect, useState } from "react";
import { useUserProfile } from "../context/UserProfileContext";
import { getCharacterRender } from "../utils/characterMedia";
import {
  fetchUserCharacters,
  fetchSoloDuoStandings,
  fetchCharacterStandings,
  cancelSoloDuoRegistrationById,
} from "../api/api";
import { toast } from "react-toastify";
import Loading from "./Loading";

function characterKey (character) {
  const name = (character.name || "").toLowerCase();
  const realm = (character.realm?.name || character.realm?.slug || "").toLowerCase();
  return `${name}-${realm}`;
}

const CharacterCard = ({
  character,
  registrations,
  onUnregisterSolo,
  onUnregisterDuo,
  isUnregistering,
}) => {
  const hasSolo = registrations?.solo != null;
  const hasDuo = registrations?.duo != null;
  const hasTournament = registrations?.tournament != null && registrations.tournament.length > 0;
  // Was mediaLinks[2] || mediaLinks[0]. Index 2 is only the full render on characters
  // that happen to have an "inset" asset; for the ~55% that do not, it was undefined and
  // this silently fell back to the 2D avatar despite a render being available.
  const mediaUrl = getCharacterRender(character.mediaLinks);

  return (
    <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl shadow-xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl min-h-[650px] flex flex-col">
      {/* Character Image Container */}
      <div className="relative h-80 overflow-hidden">
        {mediaUrl ? (
          <img
            src={mediaUrl}
            alt={character.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-400 flex items-center justify-center">
            <i className="fas fa-user text-4xl text-gray-500" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

        {/* Faction Badge */}
        {character.faction?.name && (
          <div
            className={`absolute top-4 right-4 p-2.5 rounded-full shadow-lg ${
              character.faction.name === "Horde" ? "bg-red-500" : "bg-blue-200"
            }`}
          >
            <img
              src={
                character.faction.name === "Horde"
                  ? "/horde-logo3.png"
                  : "/alliance-logo.png"
              }
              alt={character.faction.name}
              className={`w-8 h-8 object-contain ${
                character.faction.name === "Horde" ? "" : "transform scale-150"
              }`}
            />
          </div>
        )}
      </div>

      {/* Character Info */}
      <div className="flex-1 p-8 flex flex-col">
        <div className="flex flex-col items-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-3 font-serif">
            {character.name}
          </h2>
          {character.guild?.name && (
            <p className="text-blue-600 text-lg font-medium mb-4">
              ⟨{character.guild.name}⟩
            </p>
          )}

          {/* Class and Race with Icon */}
          <div className="flex items-center justify-center gap-3 mb-5 flex-nowrap w-full">
            <span className="px-3 py-2 bg-gray-700 text-white text-sm rounded-full whitespace-nowrap">
              {character.playable_class?.name ?? "—"}
            </span>
            <span className="px-3 py-2 bg-gray-700 text-white text-sm rounded-full whitespace-nowrap">
              {character.playable_race?.name ?? "—"}
            </span>
          </div>

          {/* Realm Name */}
          <p className="text-gray-600 text-lg mb-4">{character.realm?.name ?? ""}</p>
        </div>

        {/* Registrations: Solo / Duo / Tournament */}
        {(hasSolo || hasDuo || hasTournament) && (
          <div className="mb-4 space-y-2 flex flex-col items-center text-center">
            <div className="flex flex-wrap gap-2 justify-center">
              {hasSolo && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  <i className="fas fa-user" /> Solo
                </span>
              )}
              {hasDuo && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  <i className="fas fa-users" /> Duo
                </span>
              )}
              {hasTournament && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <i className="fas fa-trophy" /> Tournament
                  {registrations.tournament.length > 1 && ` (${registrations.tournament.length})`}
                </span>
              )}
            </div>
            {/* Unregister actions */}
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {hasSolo && (
                <button
                  type="button"
                  onClick={() => onUnregisterSolo(registrations.solo.registrationId)}
                  disabled={isUnregistering === registrations.solo.registrationId}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-red-600 hover:bg-red-500 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUnregistering === registrations.solo.registrationId ? (
                    <i className="fas fa-spinner fa-spin" />
                  ) : (
                    <i className="fas fa-times" />
                  )}
                  Unregister Solo
                </button>
              )}
              {hasDuo && (
                <button
                  type="button"
                  onClick={() => onUnregisterDuo(registrations.duo.registrationId)}
                  disabled={isUnregistering === registrations.duo.registrationId}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-red-600 hover:bg-red-500 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUnregistering === registrations.duo.registrationId ? (
                    <i className="fas fa-spinner fa-spin" />
                  ) : (
                    <i className="fas fa-times" />
                  )}
                  Unregister Duo
                </button>
              )}
            </div>
          </div>
        )}

        {/* Mythic+ Score */}
        <div className="mt-auto pt-8 flex items-center justify-center">
          <div className="bg-blue-500 text-white px-8 py-4 rounded-lg shadow-md">
            <i className="fas fa-star mr-4 text-xl"></i>
            <span className="font-bold text-xl">{character.mythicPlusScore ?? 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const CharacterSelection = ({ token }) => {
  const [characters, setCharacters] = useState([]);
  const [registrationsByKey, setRegistrationsByKey] = useState({});
  const [unregisteringId, setUnregisteringId] = useState(null);
  const { setUserProfile } = useUserProfile();
  const [loading, setLoading] = useState(true);

  const loadRegistrations = useCallback(async (charactersList) => {
    if (!token || !charactersList?.length) return {};
    const keyToReg = {};

    try {
      const [soloRes, duoRes] = await Promise.all([
        fetchSoloDuoStandings(token, { limit: 500, type: "solo" }),
        fetchSoloDuoStandings(token, { limit: 500, type: "duo" }),
      ]);

      const soloList = soloRes?.standings ?? [];
      const duoList = duoRes?.standings ?? [];

      soloList.forEach((entry) => {
        const key = `${(entry.characterName || "").toLowerCase()}-${(entry.characterRealm || "").toLowerCase()}`;
        keyToReg[key] = { ...keyToReg[key], solo: { registrationId: entry.registrationId } };
      });

      duoList.forEach((entry) => {
        const key = `${(entry.characterName || "").toLowerCase()}-${(entry.characterRealm || "").toLowerCase()}`;
        keyToReg[key] = { ...keyToReg[key], duo: { registrationId: entry.registrationId } };
      });

      const tournamentResults = await Promise.all(
        charactersList.map((c) =>
          fetchCharacterStandings(token, c.id)
            .then((data) => ({ id: c.id, data }))
            .catch(() => ({ id: c.id, data: null }))
        )
      );

      tournamentResults.forEach(({ id, data }) => {
        const character = charactersList.find((ch) => ch.id === id);
        if (!character) return;
        const key = characterKey(character);
        const list = Array.isArray(data) ? data : data?.standings ?? data?.entries ?? (data ? [data] : []);
        if (list.length > 0) {
          keyToReg[key] = { ...keyToReg[key], tournament: list };
        }
      });

      setRegistrationsByKey(keyToReg);
    } catch (err) {
      console.error("Error loading registrations:", err);
    }
    return keyToReg;
  }, [token]);

  useEffect(() => {
    if (!token) return;

    const fetchCharacters = async () => {
      try {
        const data = await fetchUserCharacters(token);
        const list = data || [];
        setCharacters(list);
        return list;
      } catch (error) {
        console.error("Error fetching characters:", error);
        toast.error("Error fetching characters");
        return [];
      } finally {
        setLoading(false);
      }
    };

    fetchCharacters().then((list) => {
      if (list.length > 0) loadRegistrations(list);
    });
  }, [token, loadRegistrations]);

  const handleUnregister = useCallback(
    async (registrationId) => {
      if (!token) return;
      setUnregisteringId(registrationId);
      try {
        await cancelSoloDuoRegistrationById(token, registrationId);
        toast.success("Successfully unregistered from the ladder.");
        await loadRegistrations(characters);
      } catch (error) {
        console.error("Error unregistering:", error);
        toast.error(error.message || "Failed to unregister. Please try again.");
      } finally {
        setUnregisteringId(null);
      }
    },
    [token, characters, loadRegistrations]
  );

  const registrationsForCharacter = useCallback(
    (character) => registrationsByKey[characterKey(character)] ?? null,
    [registrationsByKey]
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="container mx-auto p-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {characters.length > 0 ? (
          characters.map((character) => (
            <CharacterCard
              key={character.id}
              character={character}
              registrations={registrationsForCharacter(character)}
              onUnregisterSolo={handleUnregister}
              onUnregisterDuo={handleUnregister}
              isUnregistering={unregisteringId}
            />
          ))
        ) : (
          <p className="text-4xl font-bold text-center text-white">
            No characters found
          </p>
        )}
      </div>
    </div>
  );
};

export default CharacterSelection;
